import React, { useState, useEffect } from 'react';
import {
    Container, Grid, Card, CardContent, Typography,
    Box, CircularProgress, Alert, Button, Chip, Dialog, IconButton, Zoom,
    DialogTitle, DialogContent, DialogActions,
    ThemeProvider, createTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import NavbarMain from '../components/NavbarMain';
import { useAuth } from '../context/AuthContext';
import { programAPI, contactInfoAPI } from '../services/api';
import Footer from "../components/Footer";
import SEO from '../components/SEO';

const lightTheme = createTheme({
    palette: {
        mode: 'light',
        primary: { main: '#ff7eb9' },
        background: { default: '#fafafa', paper: '#ffffff' },
    }
});

const ProgramsPage = () => {
    const [programs, setPrograms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [contactInfo, setContactInfo] = useState(null);
    const [selectedProgram, setSelectedProgram] = useState(null);
    const [paymentDialog, setPaymentDialog] = useState({ open: false, program: null });

    const { user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        loadPrograms();
        fetchContactInfo();
    }, []);

    const loadPrograms = async () => {
        try {
            setLoading(true);
            setError('');
            const data = await programAPI.getAll();
            setPrograms(data || []);
        } catch (err) {
            console.error('❌ Error loading programs:', err);
            setError('Грешка при вчитување на програмите: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    const fetchContactInfo = async () => {
        try {
            const data = await contactInfoAPI.get();
            setContactInfo(data);
        } catch (error) {
            setContactInfo({
                linkedInUrl: 'https://linkedin.com',
                instagramUrl: 'https://www.instagram.com/angelkoska.fitness/',
            });
        }
    };

    const handlePurchase = (program, e) => {
        if (e) e.stopPropagation();

        if (user) {
            setPaymentDialog({ open: true, program });
        } else {
            navigate('/login', {
                state: {
                    message: `За да купите "${program.name}", најавете се или регистрирајте се.`,
                    redirectTo: '/buy-programs',
                    programId: program.id
                }
            });
        }
    };

    if (loading) {
        return (
            <ThemeProvider theme={lightTheme}>
                <NavbarMain />
                <Box sx={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <CircularProgress sx={{ color: '#ff7eb9' }} size={60} />
                </Box>
            </ThemeProvider>
        );
    }

    return (
        <ThemeProvider theme={lightTheme}>
            <SEO title="Фитнес Програми - BodyLab by Anamarija" description="Персонализирани фитнес програми за жени." url="/programs" />
            <NavbarMain />

            <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #ffeef8 0%, #e0f7ff 100%)', pt: { xs: 12, md: 15 }, pb: 10 }}>
                <Container maxWidth="lg">

                    <Box textAlign="center" mb={8}>
                        <Typography variant="h1" sx={{ fontWeight: 900, background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
                            Нашите Тренинг Програми
                        </Typography>
                        <Box sx={{ width: 60, height: 4, bgcolor: '#ff7eb9', mx: 'auto', mt: 2 }} />
                    </Box>

                    {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}

                    <Grid container spacing={4} alignItems="stretch">
                        {programs.map((program) => (
                            <Grid item xs={12} sm={6} md={4} key={program.id} sx={{ display: 'flex' }}>
                                <Card
                                    onClick={() => setSelectedProgram(program)}
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        cursor: 'pointer',
                                        borderRadius: '20px',
                                        transition: 'all 0.3s ease',
                                        '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 30px rgba(255,126,185,0.2)' }
                                    }}
                                >
                                    <Box sx={{ height: 6, background: 'linear-gradient(90deg, #ff7eb9, #AB47BC)' }} />
                                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 4 }}>
                                        <Typography variant="h3" sx={{ fontWeight: 800, mb: 2, fontSize: '1.4rem' }}>{program.name}</Typography>

                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: '#666',
                                                mb: 3,
                                                whiteSpace: 'pre-wrap',   // ✅ нови редови
                                                wordBreak: 'break-word'   // ✅ долги зборови/линкови
                                            }}
                                        >
                                            {program.description || ''}
                                        </Typography>

                                        <Box sx={{ mt: 'auto' }}>
                                            <Typography variant="h5" sx={{ color: '#ff7eb9', fontWeight: 900, mb: 2 }}>{program.price} ден</Typography>
                                            <Button variant="contained" fullWidth onClick={(e) => handlePurchase(program, e)} sx={{ borderRadius: '12px', bgcolor: '#ff7eb9', py: 1.5, fontWeight: 'bold' }}>
                                                🛒 Купете тука
                                            </Button>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Full Screen Modal */}
            <Dialog
                fullScreen
                open={!!selectedProgram}
                onClose={() => setSelectedProgram(null)}
                TransitionComponent={Zoom}
            >
                <Box sx={{
                    minHeight: '100vh',
                    background: 'linear-gradient(135deg, #ffeef8 0%, #e0f7ff 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 3,
                    pt: { xs: 20, md: 30 } // Го намалив pt бидејќи иксот сега е во картичката
                }}>

                    {selectedProgram && (
                        <Card sx={{
                            maxWidth: 800,
                            width: '100%',
                            borderRadius: '30px',
                            boxShadow: '0 20px 60px rgba(0,0,0,0.1)',
                            overflow: 'visible',
                            position: 'relative' // Ова е клучно за иксот да биде позициониран во однос на картичката
                        }}>
                            {/* КОПЧЕ ЗА ЗАТВОРАЊЕ - Позиционирано внатре во картичката горе десно */}
                            <IconButton
                                onClick={() => setSelectedProgram(null)}
                                sx={{
                                    position: 'absolute',
                                    top: 15,
                                    right: 15,
                                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                                    color: '#ff7eb9',
                                    '&:hover': { bgcolor: '#ff7eb9', color: 'white' },
                                    zIndex: 10,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                }}
                            >
                                <CloseIcon fontSize="medium" />
                            </IconButton>

                            <Box sx={{ height: 10, background: 'linear-gradient(90deg, #ff7eb9, #AB47BC)', borderRadius: '30px 30px 0 0' }} />

                            <CardContent sx={{ p: { xs: 4, md: 8 } }}>
                                <Chip label="Детали за програмата" sx={{ mb: 3, bgcolor: '#ff7eb9', color: 'white' }} />
                                <Typography variant="h2" sx={{ fontWeight: 900, mb: 3, color: '#1a1a1a', fontSize: { xs: '2rem', md: '3rem' }, pr: 5 }}>
                                    {selectedProgram.name}
                                </Typography>

                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontSize: '1.1rem',
                                        lineHeight: 1.8,
                                        color: '#444',
                                        mb: 4,
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word'
                                    }}
                                >
                                    {selectedProgram.description || ''}
                                </Typography>

                                <Grid container spacing={2} sx={{ mb: 4 }}>
                                    <Grid item>
                                        <Chip label={`⏱️ Траење: ${selectedProgram.durationDays} денови`} variant="outlined" />
                                    </Grid>
                                    <Grid item>
                                        <Chip label={`📋 Вежби: ${selectedProgram.days?.length || 0}`} variant="outlined" />
                                    </Grid>
                                </Grid>

                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 3 }}>
                                    <Typography variant="h3" sx={{ fontWeight: 900, color: '#ff7eb9' }}>{selectedProgram.price} ден</Typography>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        onClick={(e) => handlePurchase(selectedProgram, e)}
                                        sx={{ px: 6, py: 2, borderRadius: '15px', bgcolor: '#ff7eb9', fontWeight: 'bold' }}
                                    >
                                        Купи ја програмата
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    )}
                </Box>
            </Dialog>

            {/* ДИЈАЛОГ ЗА ПЛАЌАЊЕ */}
            <Dialog open={paymentDialog.open} onClose={() => setPaymentDialog({ open: false, program: null })} fullWidth maxWidth="sm">
                <DialogTitle sx={{ bgcolor: '#ff7eb9', color: 'white' }}>Начин на плаќање</DialogTitle>
                <DialogContent dividers sx={{ bgcolor: 'background.paper', color: 'text.primary' }}>
                    <Typography variant="body1" mb={2}>
                        За да ја купите <strong>{paymentDialog.program?.name}</strong>, уплатете <strong>{paymentDialog.program?.price} ден.</strong> на:
                    </Typography>
                    <Box sx={{ bgcolor: 'background.default', p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                        <Typography><strong>Примач:</strong> Anamarija Angelkoska</Typography>
                        <Typography><strong>Сметка:</strong> 210501755493342</Typography>
                        <Typography><strong>Банка:</strong> НЛБ Банка</Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ bgcolor: 'background.paper' }}>
                    <Button onClick={() => setPaymentDialog({ open: false, program: null })}>Затвори</Button>
                </DialogActions>
            </Dialog>

            <Footer />
        </ThemeProvider>
    );
};

export default ProgramsPage;